import type { ComponentType } from "react";
import { Link, Navigate, createBrowserRouter, type RouteObject } from "react-router";
import { Loading } from "../components/ui";
import { LoginPage, VerifyPage } from "../features/auth/LoginPage";
import { SignupPage } from "../features/auth/SignupPage";
import { PublicRunPage, RunDetailPage, RunsPage } from "../features/runs/RunsPages";
import { VendorsPage } from "../features/vendors/VendorsPage";
import { AppShell, PublicShell } from "./AppShell";

function NotFound() {
  return (
    <div className="auth">
      <h1>Page not found</h1>
      <p>
        <Link className="btn primary" to="/vendors">
          Go to vendors
        </Link>
      </p>
    </div>
  );
}

/**
 * A code-split route. Opening its URL directly loads the chunk during the router's initial hydration,
 * which renders `hydrateFallbackElement` meanwhile; without one React Router warns on every deep link.
 */
function lazyRoute(path: string, load: () => Promise<ComponentType>): RouteObject {
  return {
    path,
    hydrateFallbackElement: <Loading />,
    lazy: async () => ({ Component: await load() }),
  };
}

export const routeObjects: RouteObject[] = [
  {
    element: <PublicShell />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/signup", element: <SignupPage /> },
      { path: "/auth/verify", element: <VerifyPage /> },
      { path: "/r/:token", element: <PublicRunPage /> },
    ],
  },
  {
    element: <AppShell />,
    children: [
      { path: "/", element: <Navigate to="/vendors" replace /> },
      { path: "/vendors", element: <VendorsPage /> },
      lazyRoute("/vendors/:id", async () => (await import("../features/vendors/VendorPage")).VendorPage),
      lazyRoute("/upload", async () => (await import("../features/upload/UploadPage")).UploadPage),
      lazyRoute(
        "/payment-checks",
        async () => (await import("../features/payments/PaymentChecksPage")).PaymentChecksPage,
      ),
      lazyRoute(
        "/payment-checks/:id",
        async () => (await import("../features/payments/PaymentCheckPage")).PaymentCheckPage,
      ),
      { path: "/runs", element: <RunsPage /> },
      { path: "/runs/:id", element: <RunDetailPage /> },
      lazyRoute("/settings", async () => (await import("../features/settings/SettingsPage")).SettingsPage),
      lazyRoute("/clients", async () => (await import("../features/clients/ClientsPage")).ClientsPage),
    ],
  },
  // Staff only, with its own layout (section rail, no portal header); code-split, loaded on demand.
  lazyRoute("/admin", async () => (await import("../features/admin/AdminPage")).AdminPage),
  { path: "*", element: <NotFound /> },
];

export function createRouter() {
  return createBrowserRouter(routeObjects);
}
